import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { PhotographerFilterService } from 'src/app/services/filters/photographer-filter.service';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from '../../../../shared/components/filter-list/filter-list.component';
import { OrderQueries } from '../../../../shared/types/get-query';
import { AppService } from '../../../../app.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { IdsFilterListComponent } from 'src/app/shared/components/ids-filter-list/ids-filter-list.component';
import { Order, OrderFilters, OrderStatus, OrderType, OrderTypeEnum, ProcurementStatus } from '../../models/new/order';
import { Photographer } from '../../models/new/photographer';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import * as _ from 'lodash';
import { DateRange } from '../../models/new/dateRange';
import { Status } from '../../models/new/status';
import { OrderStatusFilterService } from 'src/app/services/filters/order-status-filter.service';
import { OrderMapper } from '../../services/order-mapper';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { Paginable } from 'src/app/shared/types/paginable';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { OrderTypeFilterService } from '../../../../services/filters/order-type-filter.service';
import { ListModalComponent } from '../company-style-list/list-modal/list-modal.component';

@Component({
  standalone: false,
  selector: 'bt-company-order-list',
  templateUrl: './company-order-list.component.html',
  styleUrls: ['./company-order-list.component.scss']
})
export class CompanyOrderListComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);

  orders: Paginable<Order> = new Paginable<Order>();
  orderFilters = new OrderFilters();
  orderStatus = OrderStatus;
  procurementStatus = ProcurementStatus;
  loading: boolean = false;

  constructor(
    private graphQlService: GraphQLService,
    private dialog: MatDialog,
    private appService: AppService,
    private route: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private photographerFilterService: PhotographerFilterService,
    private orderStatusFilterService: OrderStatusFilterService,
    private orderTypeFilterService: OrderTypeFilterService,
    private orderMapper: OrderMapper,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.getOrders();
  }

  prepareFilterOptions(params: Params) {
    //questi chiamate servono per popolare il nome delle tendine di parametri in input tramite url

    if (params['photographers'])
      this.photographerFilterService.searchById(params['photographers']).subscribe((res: Photographer[]) => {
        this.orderFilters.photographers?.forEach((photographer) => {
          const found = res.find((p) => p.id == photographer.id);
          photographer.firstName = found ? found.firstName : '';
          photographer.lastName = found ? found.lastName : '';
        });
      });

    if (params['statuses'])
      this.orderStatusFilterService.searchById(params['statuses']).subscribe((res: Status[]) => {
        this.orderFilters.statuses?.forEach((status) => {
          const found = res.find((s) => s.id == status.id);
          status.name = found ? found.name : '';
        });
      });

    if (params['orderTypes'])
      this.orderTypeFilterService.searchById(params['orderTypes']).subscribe((res: OrderType[]) => {
        this.orderFilters.orderTypes?.forEach((orderType) => {
          const found = res.find((item) => item.id == orderType.id);
          orderType.name = found ? found.name : '';
        });
      });
  }

  getOrders() {
    this.loading = true;
    this.route.queryParams.subscribe((params: Params) => {
      this.orderFilters = this.orderMapper.urlToFilters(params);

      //metodo che popola le label dei filtri in base agli id passati da url,
      this.prepareFilterOptions(params);

      this.orders.currentPage = this.orderFilters.page || 1;
      this.orders.size = this.orderFilters.size || 2;

      const pagination = this.orders.buildQuery(this.orders.currentPage);

      this.graphQlService
        .getData<any>(OrderQueries.GET_ORDERS, {
          where: this.orderMapper.toWhereClause(this.orderFilters),
          ...pagination
        })
        .pipe(
          map((res: any) => {
            const orders = res.data.orders;

            const paginable = new Paginable<Selectable<Order>>({
              items: orders.nodes.map((node: any) => ({
                ...this.orderMapper.toOrderSearch(node),
                selected: false
              })),
              totalCount: orders.totalCount,
              currentPage: this.orders.currentPage,
              size: this.orders.size
            });

            return paginable;
          })
        )
        .subscribe({
          next: (data: Paginable<Selectable<Order>>) => {
            this.orders = data;

            this.loading = false;
          },
          error: (err) => {
            this.utilitiesService.showSnackbar('Error fetching orders', SnackbarType.Error);
            this.loading = false;
            console.error(err);
          }
        });
    });
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.orders.currentPage = event.pageNumber;
    this.orders.size = event.pageSize;

    this.orderFilters.page = event.pageNumber;
    this.orderFilters.size = event.pageSize;

    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;

    this.router.navigate([location.pathname], {
      queryParams: this.orderMapper.filtersToUrl(this.orderFilters)
    });
  }

  removeOrder(order: Order) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Confirm deletion',
          message: `Are you sure you want to delete order?`
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.graphQlService
              .postData(MutationQuery.DELETE_ORDER, {
                deleteOrderInput: {
                  orderId: order.id
                }
              })
              .subscribe({
                next: (value: any) => {
                  this.utilitiesService.showSnackbar('Order deleted successfully', SnackbarType.Success);
                  this.ngOnInit();
                },
                error: (err) => {
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during deleting order',
                    SnackbarType.Error
                  );
                  console.error(err);
                }
              });
          }
        }
      });
  }

  cleanFilter(filter: string) {
    switch (filter) {
      case 'ids':
        this.orderFilters.ids = undefined;
        break;
      case 'basicNetIds':
        this.orderFilters.basicNetIds = undefined;
        break;
      case 'photographers':
        this.orderFilters.photographers = undefined;
        break;
      case 'dueDate':
        this.orderFilters.dueDate = undefined;
        break;
      case 'creationDate':
        this.orderFilters.creationDate = undefined;
        break;
      case 'completionDate':
        this.orderFilters.completionDate = undefined;
        break;
      case 'statuses':
        this.orderFilters.statuses = undefined;
        break;
      case 'orderTypes':
        this.orderFilters.orderTypes = undefined;
        break;
    }

    this.orderFilters.page = 1;

    this.refreshNavigation();
  }

  openDialogIdsFilter() {
    const dialog = this.dialog
      .open(IdsFilterListComponent, {
        data: { title: 'Order id', selected: this.orderFilters.ids }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.orderFilters.ids = res;
            this.refreshNavigation();
          }
        }
      });
  }

  openDialogBasicNetIdsFilter() {
    this.dialog
      .open(IdsFilterListComponent, {
        data: { title: 'BasicNet ID', selected: this.orderFilters.basicNetIds }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.orderFilters.basicNetIds = res;
            this.orderFilters.page = 1;
            this.refreshNavigation();
          }
        }
      });
  }

  openPhotographerFilterModal(): void {
    const dialog = this.dialog
      .open(FilterListComponent<Photographer>, {
        data: {
          title: 'Photographers',

          selected: this.orderFilters.photographers
            ? this.orderFilters.photographers.map((el) => ({
                ...el,
                displayId: el.id,
                displayName: el.firstName + ' ' + el.lastName,
                selected: true
              }))
            : [],
          filterService: this.photographerFilterService
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: Array<Selectable<Photographer>>) => {
          if (res) {
            this.orderFilters.photographers = res.map(
              (el: any) => new Photographer({ id: el.displayId, firstName: el.displayName, lastName: '' })
            );

            this.refreshNavigation();
          }
        }
      });
  }

  openDueDateFilterModal() {
    this.dialog
      .open(DateFilterListComponent, {
        data: {
          title: 'Due Date',
          selected: _.cloneDeep(this.orderFilters.dueDate)
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: DateRange) => {
          if (res) {
            this.orderFilters.dueDate = res;
            this.refreshNavigation();
          }
        }
      });
  }

  openCreationDateFilterModal() {
    this.dialog
      .open(DateFilterListComponent, {
        data: {
          title: 'Creation Date',
          selected: _.cloneDeep(this.orderFilters.creationDate)
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: DateRange) => {
          if (res) {
            this.orderFilters.creationDate = res;
            this.refreshNavigation();
          }
        }
      });
  }

  openCompletionDateFilterModal() {
    this.dialog
      .open(DateFilterListComponent, {
        data: {
          title: 'Completion Date',
          selected: _.cloneDeep(this.orderFilters.completionDate)
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: DateRange) => {
          if (res) {
            this.orderFilters.completionDate = res;
            this.refreshNavigation();
          }
        }
      });
  }

  openStatusFilterModal(): void {
    const dialog = this.dialog
      .open(FilterListComponent<Status>, {
        data: {
          title: 'Statuses',

          selected: this.orderFilters.statuses
            ? this.orderFilters.statuses.map((el) => ({
                ...el,
                displayId: el.id,
                displayName: el.name,
                selected: true
              }))
            : [],
          filterService: this.orderStatusFilterService
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: Array<Selectable<Status>>) => {
          if (res) {
            this.orderFilters.statuses = res.map((el: any) => new Status({ id: el.displayId, name: el.displayName }));
            this.refreshNavigation();
          }
        }
      });
  }

  openOrderTypeFilterModal(): void {
    this.dialog
      .open(FilterListComponent<OrderType>, {
        data: {
          title: 'Order Types',
          selected: this.orderFilters.orderTypes
            ? this.orderFilters.orderTypes.map((el) => ({
                ...el,
                displayId: el.id,
                displayName: el.name,
                selected: true,
                disabled: false
              }))
            : [],
          filterService: this.orderTypeFilterService
        },
        width: '400px'
      })
      .afterClosed()
      .subscribe({
        next: (res: Array<Selectable<OrderType>>) => {
          if (res) {
            this.orderFilters.orderTypes = res.map(
              (el) => new OrderType({ id: el.id as OrderTypeEnum, name: el.name })
            );
            this.refreshNavigation();
          }
        }
      });
  }

  getOrderTypeLabel(orderType?: OrderTypeEnum): string {
    switch (orderType) {
      case OrderTypeEnum.Complete:
        return 'Complete';
      case OrderTypeEnum.Dressed:
        return 'On Model';
      case OrderTypeEnum.StillLife:
        return 'Still Life';
      default:
        return '-';
    }
  }

  getProcurementStatusLabel(order: Order): string {
    switch (order.procurement?.status) {
      case ProcurementStatus.Pending:
        return 'Pending';
      case ProcurementStatus.Sent:
        return 'Sent';
      case ProcurementStatus.Skipped:
        return 'Skipped';
      case ProcurementStatus.Error:
        return 'Error';
      default:
        return '';
    }
  }

  getProcurementStatusChipClass(order: Order): string {
    switch (order.procurement?.status) {
      case ProcurementStatus.Pending:
        return 'grey primary';
      case ProcurementStatus.Sent:
        return 'bg-lightest-blue primary';
      case ProcurementStatus.Error:
        return 'red white';
      default:
        return 'grey';
    }
  }

  openOrderedSkusModal(orderId: number): void {
    this.graphQlService
      .getData<any>(OrderQueries.GET_ORDERED_SKUS, { orderId })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: any) => {
          this.openListModal('Ordered SKUs', (data.data.orderedSkus as string[]) || []);
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error fetching ordered SKUs', SnackbarType.Error);
          console.error(err);
        }
      });
  }

  openShippedSkusModal(orderId: number): void {
    this.graphQlService
      .getData<any>(OrderQueries.GET_SHIPPED_SKUS, { orderId })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: any) => {
          this.openListModal('Shipped SKUs', (data.data.shippedSkus as string[]) || []);
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error fetching shipped SKUs', SnackbarType.Error);
          console.error(err);
        }
      });
  }

  openListModal(title: string, list: Array<string>): void {
    this.dialog.open(ListModalComponent, {
      data: {
        title,
        list
      }
    });
  }
}
